import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Skeleton } from "@mui/material";
import { Spinner } from "../../components";
import FirebaseService from "../../service/FirebaseService";
import AlertService from "../../service/AlertService";
import ToastMessages from "../../model/enum/ToastMessages";
import GeocodeService from "../../service/GeocodeService";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import DeleteModal from "../modal/DeleteModal";
import EditIcon from "../../assets/admin/edit-icon.svg";
import FullscreenImage from "../../shared/components/images/FullscreenImage";
import CloseIcon from "@mui/icons-material/Close";
import Service from "../../model/service";
import Review from "../../model/review";
import ApiService from "../../service/ApiService";
import PageResultResponse from "../../model/pageResultResponse";
import Misc from "../../utils/Misc";
import useStyles from "./styles";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "85%",
  },
};

interface Props {
  item: Review;
  resetList?: () => void;
}

const ReviewModal = ({ item, resetList }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [serviceId, setServiceId] = useState(item.Service?.id);
  const [services, setServices] = useState<Service[]>([]);
  const [location, setLocation] = useState(item.location);
  const [description, setDescription] = useState(item.description);
  const [loading, setLoading] = useState(true);
  const link = item.imageUrl;

  //Get all services from the database
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await ApiService.get<PageResultResponse>(DatabaseEntities.SERVICES)
        .then((data: PageResultResponse) => {
          if (Misc.isArrayEmpty(data.items)) return;
          setServices(data.items);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (loading && Misc.isArrayEmpty(services)) fetchData();
  }, [services]);

  const handleService = (e: any) => setServiceId(e.target.value);
  const handleLocation = (e: any) => setLocation(e.target.value);
  const handleDescription = (e: any) => setDescription(e.target.value);
  const toggleFullScreen = () => setFullScreen((fullScreen) => !fullScreen);

  const handleUpdate = async (payload: Review) => {
    await ApiService.put<Review>(
      `${DatabaseEntities.REVIEWS}/${payload.id}`,
      payload
    )
      .then(() => {
        AlertService.success(ToastMessages.UPDATED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: Review) => {
    await ApiService.delete<any>(`${DatabaseEntities.REVIEWS}/${payload.id}`)
      .then(async () => {
        AlertService.success(ToastMessages.DELETED);

        await FirebaseService.deleteFile(payload.imageUrl).catch(() => {
          AlertService.error(ToastMessages.FIREBASE_FILE__DELETE_ERROR);
        });
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const submit = async () => {
    if (!serviceId || !location) return;

    await GeocodeService.fromAddressToLatLng(location).then(
      async (response: any) => {
        const { lat, lng } = response.results[0].geometry.location;

        let payload: Review = {
          id: item.id,
          serviceId: serviceId,
          description: description,
          imageUrl: link,
          latitude: lat,
          longitude: lng,
          location: location,
          finishedAt: item.finishedAt,
        };

        await handleUpdate(payload);
        handleClose();
      }
    );
  };

  return (
    <div>
      <Button onClick={handleOpen}>
        <img src={EditIcon} alt="edit icon" />
      </Button>
      {fullScreen ? (
        <FullscreenImage imageUrl={link} toggleFullScreen={toggleFullScreen} />
      ) : (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ overflowY: "auto" }}
        >
          {loading ? (
            <Spinner />
          ) : (
            <Box sx={style}>
              <div className={classes.headerDiv}>
                <DeleteModal
                  item={item}
                  service={DatabaseEntities.REVIEWS}
                  handleDelete={handleDelete}
                />
                <Button onClick={handleClose} sx={{ color: "black" }}>
                  <CloseIcon />
                </Button>
              </div>
              <hr />
              <div className={classes.form}>
                <div className={classes.container}>
                  <div className="d-flex row">
                    <div className="mt-2">
                      <Typography className={classes.label}>Service</Typography>
                      <select
                        className={classes.input}
                        onChange={handleService}
                        value={serviceId}
                      >
                        <option value="">Select a service</option>
                        {services.map((service: Service) => {
                          return (
                            <option key={service.id} value={service.id}>
                              {service.name}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="mt-2">
                      <Typography className={classes.label}>
                        Location
                      </Typography>
                      <input
                        className={classes.input}
                        onChange={handleLocation}
                        value={location}
                      />
                    </div>
                    <div className="mt-2">
                      <Typography className={classes.label}>
                        Description
                      </Typography>
                      <textarea
                        className={classes.input}
                        onChange={handleDescription}
                        value={description}
                      />
                    </div>
                    <div className="mt-2">
                      <Typography className={classes.label}>Link</Typography>
                      <textarea
                        className={classes.textareaDetail}
                        value={link}
                        disabled
                      />
                    </div>
                  </div>
                  {link ? (
                    <img
                      className={classes.image}
                      src={link}
                      alt="review img"
                      onClick={toggleFullScreen}
                    />
                  ) : (
                    <Skeleton variant="rectangular" width={1000} height={500} />
                  )}
                </div>
              </div>
              <div className={classes.buttonDiv}>
                <Btn className={classes.button} onClick={submit}>
                  Save
                </Btn>
              </div>
            </Box>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ReviewModal;
